import React, { useContext, useState, useEffect } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  TextField,
  Stack,
  Autocomplete,
  Box,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { UserStateContext } from "../../context/user-state-context.js";

export const CompanySelector = ({
  open,
  onClose,
  onSubmit,
  title,
  permission,
}) => {
  const { user, asCompany } = useContext(UserStateContext);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (!user?.permissions) return;

    setAvailableCompanies(
      user.companies
        .sort((a, b) => (a.name > b.name ? 1 : -1))
        .filter(function (c) {
          const found =
            user.permissions["*"] ||
            user.permissions[permission] === "*" ||
            user.permissions?.[permission]?.indexOf(c.id) > -1;

          return found;
        })
    );
  }, [user, permission]);

  // Set default selected company to asCompany when dialog opens
  useEffect(() => {
    if (open && asCompany && availableCompanies.length > 0) {
      const defaultCompany = availableCompanies.find(
        (c) => c.id === asCompany.id
      );
      if (defaultCompany) {
        setSelectedCompany(defaultCompany);
      }
    }
  }, [open, asCompany, availableCompanies]);

  return (
    <Dialog
      PaperProps={{
        style: { borderRadius: "28px", width: "460px" },
      }}
      open={open}
      onClose={onClose}
    >
      <DialogTitle sx={{ m: 0, p: 2, fontSize: "medium" }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Stack alignItems="center" component="span">
          <Typography variant="h4" component="span">
            {title}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Typography sx={{ mb: 1, fontSize: "14px" }}>
            Select the appropriate company.
          </Typography>
          <Autocomplete
            value={selectedCompany}
            onChange={(event, newValue) => {
              setSelectedCompany(newValue);
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            options={availableCompanies}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search for a company..."
                variant="outlined"
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography>{option.name}</Typography>
              </Box>
            )}
            sx={{ width: "100%" }}
            noOptionsText="No companies found"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ py: 1 }}>
        <Button
          onClick={onClose}
          variant="text"
          sx={{
            borderRadius: "20px",
            color: "rgba(29, 37, 45, 0.7)",
            width: "75px",
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={!selectedCompany}
          onClick={() => onSubmit(selectedCompany)}
          variant="text"
          sx={{
            mr: 1,
            borderRadius: "20px",
            width: "75px",
            color: "#53A6D6",
            textTransform: "none",
          }}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};
